import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import camelCaseRecursive from 'camelcase-keys-recursive';
import snakeCaseKeys from 'snakecase-keys';
import { ComponentDataTable, DefaultModal, SimpleCenteredModal } from '../../components';
import { sendAlert } from '../../actions/utils';
import {
  indexRiskPreventionDocumentRequest,
  updateRiskPreventionDocumentRequest,
  destroyRiskPreventionDocumentRequest
} from '../../requests/riskPreventionDocuments';
import columns from './customColumns';
import RiskPreventionDocumentForm from './RiskPreventionDocumentForm';

const RiskPreventionCustomsDataTable = ({ moreData, config }) => {
  const dispatch = useDispatch();
  const [onRequest, setOnRequest] = useState(true);
  const [total, setTotal] = useState(0);
  const [documents, setDocuments] = useState([]);
  const [query, setQuery] = useState(null);

  const [modalShow, setModalShow] = useState(false);
  const [modalTitle, setModalTitle] = useState('');
  const [modalBody, setModalBody] = useState('');
  const [modalAction, setModalAction] = useState(() => null);
  const [modalItem, setModalItem] = useState({});

  const [simpleModalShow, setSimpleModalShow] = useState(false);
  const [simpleModalTitle, setSimpleModalTitle] = useState('');
  const [simpleModalBody, setSimpleModalBody] = useState(null);
  const [size, setSize] = useState('lg');
  const defaultConfig = {
    endpoints: {
      index: indexRiskPreventionDocumentRequest,
      update: updateRiskPreventionDocumentRequest,
      destroy: destroyRiskPreventionDocumentRequest
    },
    fieldName: 'risk_prevention',
    kind: 'customs'
  };

  const activeConfig = config || defaultConfig;

  const handleSuccessIndex = response => {
    const { data: rows, metadata } = response.data;
    setDocuments(rows);
    setTotal(metadata?.amount ?? rows?.length ?? 0);
    setOnRequest(false);
  };

  const handleIndexRequest = (params = {}) => {
    setOnRequest(true);
    setQuery(params);
    activeConfig.endpoints.index({
      dispatch,
      params: { ...params, kind: activeConfig.kind },
      successCallback: handleSuccessIndex
    });
  };

  const handleFailureRequest = error => {
    const { response } = error || {};
    setOnRequest(false);
    setModalShow(false);
    setSimpleModalShow(false);
    dispatch(sendAlert({ kind: 'error', message: response?.data?.message || 'Ocurrió un error.' }));
  };

  const handleModalClose = () => setSimpleModalShow(false);
  const handleDefaultModalClose = () => setModalShow(false);

  const handleSuccessRequest = status => {
    let message;
    switch (status) {
      case 'deleted':
        message = 'Documento eliminado con éxito';
        break;
      case 'updated':
        message = 'Documento actualizado con éxito';
        break;
      default:
        message = 'Operación completada';
    }

    dispatch(sendAlert({ kind: 'success', message }));
    handleIndexRequest(query || {});
    handleModalClose();
  };

  const handleDeleteRequest = item => {
    setOnRequest(true);
    activeConfig.endpoints.destroy(item.id, {
      dispatch,
      successCallback: () => handleSuccessRequest('deleted'),
      failureCallback: handleFailureRequest,
      callback: () => setOnRequest(false)
    });
    handleDefaultModalClose();
  };

  const handleDeleteModal = item => {
    setModalShow(true);
    setModalTitle('Eliminar Documento');
    setModalItem(item);
    setModalAction(() => handleDeleteRequest);
    setModalBody('¿Estás seguro que deseas eliminar este documento?');
  };

  const handleUpdateRequest = values => {
    setOnRequest(true);
    const id = values?.id;
    const params = snakeCaseKeys(values);

    const { fieldName } = activeConfig;
    const formFieldName = fieldName === 'risk_prevention' ? 'riskPrevention' : 'supportDocument';

    const newFile = values?.[formFieldName]?.document;
    if (newFile) {
      if (!params[fieldName]) params[fieldName] = {};
      params[fieldName].document = newFile;
    } else if (params?.[fieldName]) {
      delete params[fieldName].document;
    }

    activeConfig.endpoints.update(id, {
      dispatch,
      params,
      formData: true,
      successCallback: () => handleSuccessRequest('updated'),
      failureCallback: handleFailureRequest,
      callback: () => setOnRequest(false)
    });
  };

  const handleUpdateModal = item => {
    const fieldName = activeConfig.fieldName === 'risk_prevention' ? 'riskPrevention' : 'supportDocument';
    const parsedItem = camelCaseRecursive({
      id: item?.id || null,
      name: item?.name || '',
      isPublic: !!item?.is_public
    });

    setSimpleModalBody(
      <RiskPreventionDocumentForm
        action="edit"
        id={parsedItem.id}
        name={parsedItem.name}
        isPublic={parsedItem.isPublic}
        formRequest={handleUpdateRequest}
        handleModalClose={handleModalClose}
        fieldName={fieldName}
      />
    );
    setSimpleModalTitle('Editar Documento');
    setSimpleModalShow(true);
    setSize('lg');
  };

  const openInNewTab = url => window.open(url, '_blank', 'noopener,noreferrer');

  const handleViewItem = item => {
    const fileUrl = item?.document_info?.file_url;
    if (fileUrl) {
      openInNewTab(fileUrl);
    } else {
      dispatch(sendAlert({ kind: 'warning', message: 'Este documento no tiene archivo para visualizar.' }));
    }
  };

  const handleDownloadRequest = item => {
    const fileUrl = item?.document_info?.file_url;
    if (fileUrl) {
      window.open(fileUrl, '_blank', 'noopener,noreferrer');
      dispatch(sendAlert({ kind: 'success', message: 'Documento abierto en una nueva pestaña' }));
    } else {
      dispatch(sendAlert({ kind: 'warning', message: 'Este documento no tiene archivo para descargar.' }));
    }
    handleDefaultModalClose();
  };

  const handleDownloadModal = item => {
    setModalShow(true);
    setModalTitle('Descargar Documento');
    setModalItem(item);
    setModalAction(() => handleDownloadRequest);
    setModalBody('¿Quieres descargar este documento?');
  };

  const handleActions = (item, action) => {
    switch (action) {
      case 'show':
        handleViewItem(item);
        break;
      case 'download':
        handleDownloadModal(item);
        break;
      case 'edit':
        handleUpdateModal(item);
        break;
      case 'destroy':
        handleDeleteModal(item);
        break;
      default:
        // eslint-disable-next-line no-console
        console.log('Acción no reconocida:', action);
        break;
    }
  };

  return (
    <>
      <div className="data-table-yellow">
        <ComponentDataTable
          onRequest={onRequest}
          columns={columns(handleActions)}
          data={documents}
          totalRows={total}
          resourceRequest={handleIndexRequest}
          moreData={moreData}
          preName={activeConfig.fieldName === 'risk_prevention' ? 'risk_prevention_documents' : 'support_documents'}
          pointerOnHover
        />
      </div>

      <DefaultModal
        title={modalTitle}
        body={modalBody}
        show={modalShow}
        handleClose={handleDefaultModalClose}
        handleConfirm={() => modalAction(modalItem)}
        titleBtnClose="Cancelar"
        titleBtnSave="Confirmar"
        disabled={onRequest}
      />

      <SimpleCenteredModal
        title={simpleModalTitle}
        body={simpleModalBody}
        size={size}
        show={simpleModalShow}
        onHide={handleModalClose}
      />
    </>
  );
};

export default RiskPreventionCustomsDataTable;
